
import Vue, { PropType } from "vue";
import draggable from "vuedraggable";

interface TableHeader {
  text: string;
  value: string;
  visible?: boolean;
  sortable?: boolean;
  align?: string;
  width?: string;
}

export default Vue.extend({
  name: "ColumnManager",
  components: {
    draggable
  },
  props: {
    headers: {
      type: Array as PropType<TableHeader[]>,
      required: true
    },
    storageKey: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      localHeaders: [] as TableHeader[]
    };
  },
  created() {
    this.loadColumnConfig();
  },
  methods: {
    loadColumnConfig() {
      const saved = localStorage.getItem(this.storageKey);
      if (saved) {
        try {
          const config = JSON.parse(saved);
          // Create a map of current headers for quick lookup
          const currentHeadersMap = this.headers.reduce((acc: any, h) => {
            acc[h.value] = h;
            return acc;
          }, {});

          // Merge saved config with current headers
          const savedHeaders = config
            .map((savedItem: any) => {
              const currentHeader = currentHeadersMap[savedItem.value];
              if (currentHeader) {
                return {
                  ...currentHeader,
                  visible: savedItem.visible !== false
                };
              }
              return null;
            })
            .filter((h: any) => h !== null);

          // Add any new headers that weren't in saved config
          const savedValues = new Set(config.map((h: any) => h.value));
          const newHeaders = this.headers
            .filter(h => !savedValues.has(h.value))
            .map(h => ({ ...h, visible: true }));

          this.localHeaders = [...savedHeaders, ...newHeaders];
        } catch {
          this.localHeaders = this.headers.map(h => ({ ...h, visible: true }));
        }
      } else {
        this.localHeaders = this.headers.map(h => ({ ...h, visible: true }));
      }
    },
    onDragEnd() {
      this.emitHeaders();
    },
    onVisibilityChange() {
      this.emitHeaders();
    },
    emitHeaders() {
      const visibleHeaders = this.localHeaders
        .filter(h => h.visible !== false)
        .map(({ visible, ...header }) => header);
      this.$emit("update:headers", visibleHeaders);
    },
    saveColumns() {
      const config = this.localHeaders.map(h => ({
        value: h.value,
        text: h.text,
        visible: h.visible !== false
      }));
      localStorage.setItem(this.storageKey, JSON.stringify(config));
      this.emitHeaders();
      this.$emit("saved");
      
      // Show snackbar
      this.$emit("snackbar", {
        text: "Column preferences saved",
        color: "#13CA82"
      });
    },
    resetColumns() {
      localStorage.removeItem(this.storageKey);
      this.localHeaders = this.headers.map(h => ({ ...h, visible: true }));
      this.emitHeaders();
      
      // Show snackbar
      this.$emit("snackbar", {
        text: "Columns reset to default",
        color: "#0B4598"
      });
    }
  },
  watch: {
    headers: {
      handler(newHeaders) {
        if (newHeaders && newHeaders.length > 0) {
          this.loadColumnConfig();
        }
      },
      deep: true,
      immediate: false
    }
  }
});
